import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { merge, Subscription, switchMap } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectsFeatureService } from '../../features/projects-feature/projects-feature.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Project } from '../../domain/projects-domain';
import { Phase } from '../../domain/phases-domain';
import { Task } from '../../domain/tasks-domain';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import moment from 'moment';
moment.updateLocale(moment.locale(), { week: { dow: 1, doy: 4 } });

const DATE_FORMATS = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: { dateInput: 'DD/MM/YYYY', monthYearLabel: 'MMM YYYY', dateA11yLabel: 'LL', monthYearA11yLabel: 'MMMM YYYY' },
};
import { InsertTimesheetLineRequestParams } from '../../domain/timesheets-domain';
import dayjs from 'dayjs';
import { DateFormats } from '../../constants/datetime.constants';
import { TimesheetsFeatureService } from '../../features/timesheets-feature/timesheets-feature.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AsyncPipe } from '@angular/common';
import { SingleDropdownComponent } from '../../components/elements/dropdown/single-dropdown.component';

@Component({
  selector: 'app-fill-hours-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatProgressSpinnerModule,
    NgxMaskDirective,
    AsyncPipe,
    SingleDropdownComponent,
  ],
  providers: [provideMomentDateAdapter(DATE_FORMATS), provideNgxMask()],
  templateUrl: './fill-hours-dialog.component.html',
  styleUrl: './fill-hours-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FillHoursDialogComponent implements OnInit, OnDestroy {
  _projectsFeature = inject(ProjectsFeatureService);
  _timesheetFeature = inject(TimesheetsFeatureService);
  _dialog = inject(MatDialog);
  _snackBar = inject(MatSnackBar);

  readonly dateInput = new FormControl(moment(), [Validators.required]);
  readonly projectInput = new FormControl<Project | null>(null, [Validators.required]);
  readonly phaseInput = new FormControl<Phase | null>(null, [Validators.required]);
  readonly taskInput = new FormControl<Task | null>(null, [Validators.required]);
  readonly timeInput = new FormControl<string | null>(null, [Validators.required, this.maximumHoursValidator(24), this.minHoursValidator(0.01)]);
  readonly commentInput = new FormControl<string>('', [Validators.required]);

  errorDateInputMessage = signal('');
  errorProjectInputMessage = signal('');
  errorPhaseInputMessage = signal('');
  errorTaskInputMessage = signal('');
  errorCommentInputMessage = signal('');
  errorTimeInputMessage = signal('');

  submitEntrySubscription?: Subscription;

  constructor() {
    merge(this.dateInput.statusChanges, this.dateInput.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateDateInputErrorMessage());

    merge(this.projectInput.statusChanges, this.projectInput.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const currentProject = this.projectInput.value;
        const currentPhase = this.phaseInput.value;
        if (!currentProject || (currentPhase && currentPhase.projectNo !== currentProject.projectNo)) {
          this.phaseInput.patchValue(null);
          this.taskInput.patchValue(null);
        }
        this.updateProjectInputErrorMessage();
      });

    merge(this.phaseInput.statusChanges, this.phaseInput.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.updatePhaseInputErrorMessage();
        const phase = this.phaseInput.value;
        const currentTask = this.taskInput.value;
        if (!currentTask || !phase || currentTask.phaseNo !== phase.phaseNo || currentTask.projectNo !== phase.projectNo) {
          this.taskInput.patchValue(null);
        }
        if (phase && this.projectInput.value?.projectNo !== phase.projectNo) {
          const project = this._projectsFeature.projects().find((p) => p.projectNo === phase.projectNo);
          if (project) this.projectInput.patchValue(project);
        }
      });

    merge(this.taskInput.statusChanges, this.taskInput.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.updateTaskInputErrorMessage();
        const task = this.taskInput.value;
        if (task) {
          if (this.phaseInput.value?.phaseNo !== task.phaseNo || this.phaseInput.value?.projectNo !== task.projectNo) {
            const phase = this._projectsFeature.phases().find((p) => p.phaseNo === task.phaseNo && p.projectNo === task.projectNo);
            if (phase) this.phaseInput.patchValue(phase);
          }
          if (this.projectInput.value?.projectNo !== task.projectNo) {
            const project = this._projectsFeature.projects().find((p) => p.projectNo === task.projectNo);
            if (project) this.projectInput.patchValue(project);
          }
        }
      });

    merge(this.timeInput.statusChanges, this.timeInput.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateTimeInputErrorMessage());

    merge(this.commentInput.statusChanges, this.commentInput.valueChanges)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.updateCommentInputErrorMessage());
  }

  ngOnInit() {
    const selectedTimesheetLine = this._timesheetFeature.selectedTimesheetLine();
    const copyTimesheetLine = this._timesheetFeature.copyTimesheetLine();
    const preset = this._timesheetFeature.presetProjectFilter();
    const projectNo = preset?.projectNo ?? '';
    const phaseNo = preset?.phaseNo ?? '';
    const taskNo = preset?.taskNo ?? '';

    const lineToPrePopulate = selectedTimesheetLine ?? copyTimesheetLine;
    if (lineToPrePopulate) {
      this.dateInput.patchValue(moment(lineToPrePopulate.activityDate));
      this.projectInput.patchValue(this._projectsFeature.projects().find((project) => project.projectNo == lineToPrePopulate.projectNo) ?? null);
      this.phaseInput.patchValue(this._projectsFeature.phases().find((phase) => phase.phaseNo == lineToPrePopulate.phaseNo && phase.projectNo === lineToPrePopulate.projectNo) ?? null);
      this.taskInput.patchValue(
        this._projectsFeature
          .tasks()
          .find((task) => task.taskNo == lineToPrePopulate.taskNo && task.phaseNo === lineToPrePopulate.phaseNo && task.projectNo === lineToPrePopulate.projectNo) ?? null,
      );
      this.timeInput.patchValue(this.decimalToTime(lineToPrePopulate.totalTime));
      this.commentInput.patchValue(lineToPrePopulate.comment);
    }

    if (projectNo) {
      this.projectInput.patchValue(this._projectsFeature.projects().find((project) => project.projectNo == projectNo) ?? null);
    }

    if (phaseNo) {
      this.phaseInput.patchValue(this._projectsFeature.phases().find((phase) => phase.phaseNo == phaseNo && phase.projectNo === projectNo) ?? null);
    }

    if (taskNo) {
      this.taskInput.patchValue(this._projectsFeature.tasks().find((task) => task.taskNo == taskNo && task.phaseNo === phaseNo && task.projectNo === projectNo) ?? null);
    }
  }

  ngOnDestroy() {
    this._timesheetFeature.selectedTimesheetLine.set(null);
    this._timesheetFeature.copyTimesheetLine.set(null);
    this._timesheetFeature.presetProjectFilter.set(null);
    this.submitEntrySubscription?.unsubscribe();
  }

  protected readonly filterPhasesByProjectNo = this._projectsFeature.filterPhasesByProjectNo;

  get taskOptions(): Task[] {
    const project = this.projectInput.value;
    const phase = this.phaseInput.value;
    const tasks = this._projectsFeature.tasks();
    if (phase) return tasks.filter((t) => t.phaseNo === phase.phaseNo && t.projectNo === phase.projectNo);
    if (project) return tasks.filter((t) => t.projectNo === project.projectNo);
    return tasks;
  }

  maximumHoursValidator(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === '') return null;

      const numValue = this.timeToDecimal(value);
      return !isNaN(numValue) && numValue > max ? { maxHour: { max, actual: numValue } } : null;
    };
  }

  minHoursValidator(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === '') return null;

      const numValue = this.timeToDecimal(value);
      return !isNaN(numValue) && numValue < min ? { minHour: { min, actual: numValue } } : null;
    };
  }

  timeToDecimal(time: string) {
    const hours = parseInt(time.substring(0, 2), 10); // First 2 digits
    const minutes = parseInt(time.substring(3, 5), 10);

    return hours + minutes / 60;
  }

  decimalToTime(duration: number) {
    const hours = Math.floor(duration); // Extract the whole number part (hours)
    const minutes = Math.round((duration - hours) * 60); // Extract the decimal part and convert to minutes

    // Format the hours and minutes to always be 2 digits (e.g., 05 instead of 5)
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');

    return `${formattedHours}:${formattedMinutes}`;
  }

  displayProjectInputValue(selectedProject: Project): string {
    return selectedProject ? `${selectedProject.projectNo} - ${selectedProject.projectName}` : '';
  }

  displayPhaseInputValue(selectedPhase: Phase): string {
    return selectedPhase ? `${selectedPhase.phaseNo} - ${selectedPhase.phaseName}` : '';
  }

  displayTaskInputValue(selectedTask: Task): string {
    return selectedTask ? `${selectedTask.taskNo} - ${selectedTask.taskName}` : '';
  }

  updateDateInputErrorMessage() {
    if (this.dateInput.hasError('required')) {
      this.errorDateInputMessage.set('Please select a date.');
    } else {
      this.errorDateInputMessage.set('');
    }
  }

  updateProjectInputErrorMessage() {
    if (this.projectInput.hasError('required')) {
      this.errorProjectInputMessage.set('Please select a project.');
    } else {
      this.errorProjectInputMessage.set('');
    }
  }

  updatePhaseInputErrorMessage() {
    if (this.phaseInput.hasError('required')) {
      this.errorPhaseInputMessage.set('Please select a phase.');
    } else {
      this.errorPhaseInputMessage.set('');
    }
  }

  updateTaskInputErrorMessage() {
    if (this.taskInput.hasError('required')) {
      this.errorTaskInputMessage.set('Please select a task.');
    } else {
      this.errorTaskInputMessage.set('');
    }
  }

  updateTimeInputErrorMessage() {
    if (this.timeInput.hasError('required')) {
      this.errorTimeInputMessage.set('Please input a duration.');
    } else if (this.timeInput.hasError('minHour')) {
      this.errorTimeInputMessage.set('Minimum duration is above zero.');
    } else if (this.timeInput.hasError('maxHour')) {
      this.errorTimeInputMessage.set('Maximum duration is 24 hours.');
    } else {
      this.errorTimeInputMessage.set('');
    }
  }

  updateCommentInputErrorMessage() {
    if (this.commentInput.hasError('required')) {
      this.errorCommentInputMessage.set('Please input a comment.');
    } else {
      this.errorCommentInputMessage.set('');
    }
  }

  onFormatTimeDuration() {
    if (this.timeInput.value) {
      if (!this.timeInput.value.includes(':')) {
        // Handle decimal input
        this.timeInput.patchValue(this.decimalToTime(parseFloat(this.timeInput.value)));
      } else {
        // Handle time format input with colon
        const timeParts = this.timeInput.value.split(':');
        if (timeParts.length === 2) {
          // Format to ensure hh:mm pattern
          const hours = timeParts[0].padStart(2, '0');
          const minutes = timeParts[1].padStart(2, '0');
          this.timeInput.patchValue(`${hours}:${minutes}`);
        }
      }
    } else {
      // Handle empty input
      this.timeInput.patchValue(this.decimalToTime(0));
    }
  }

  onSubmitTimesheetEntry() {
    const entryDate = dayjs(this.dateInput.value?.toDate());
    const totalTime = this.timeToDecimal(this.timeInput.value ?? '0');

    const buildData = (timesheetNo: string): InsertTimesheetLineRequestParams => ({
      activityDate: entryDate.format(DateFormats.DATE_YEAR_MONTH_DAY_KABOB),
      projectNo: this.projectInput.value?.projectNo ?? '',
      phaseNo: this.phaseInput.value?.phaseNo ?? '',
      taskNo: this.taskInput.value?.taskNo ?? '',
      totalTime,
      comment: this.commentInput.value ?? '',
      timesheetNo,
      billable: 'True',
    });

    const resolveTimesheetNo$ = this._timesheetFeature.resolveTimesheetNo(entryDate);

    // add a new timesheet if no timesheet line is selected
    if (!this._timesheetFeature.selectedTimesheetLine()) {
      // validate if it is within 24 hours
      if (this._timesheetFeature.validateNewEntryHoursLog(entryDate, totalTime)) {
        this.submitEntrySubscription = resolveTimesheetNo$.pipe(
          switchMap((timesheetNo) => this._timesheetFeature.submitTimesheetEntry(buildData(timesheetNo))),
        ).subscribe({
          next: () => {
            this._snackBar.open('Added timesheet entry successfully', undefined, {
              duration: 3000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
            });
            this._dialog.closeAll();
          },
        });
      } else {
        this._snackBar.open('You have already logged the maximum of 24 hours for the selected date.', undefined, {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
        });
      }
    } else {
      if (this._timesheetFeature.validateOldEntryHoursLog(entryDate, totalTime, this._timesheetFeature.selectedTimesheetLine()?.entryNo)) {
        // update the timesheet that has been selected
        this.submitEntrySubscription = resolveTimesheetNo$.pipe(
          switchMap((timesheetNo) => this._timesheetFeature.updateTimesheetEntry({ entryNo: this._timesheetFeature.selectedTimesheetLine()?.entryNo, ...buildData(timesheetNo) })),
        ).subscribe({
          next: () => {
            this._snackBar.open('Updated timesheet entry successfully', undefined, {
              duration: 3000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
            });
            this._dialog.closeAll();
          },
        });
      } else {
        this._snackBar.open('The updated duration exceeds the 24-hour maximum allowed for the selected date.', undefined, {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
        });
      }
    }
  }

  openDeleteEntryDialog() {
    this._timesheetFeature.handleOpeningDeleteTimesheetLineDialog();
  }
}
